import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ConfirmationModal from '../components/ConfirmationModal';
import { initializePaddle, Paddle } from '@paddle/paddle-js';
import BackButton from '../components/BackButton';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';

type PricingPlan = 'monthly' | 'annual';

const SubscriptionPage: React.FC = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation(['subscription', 'common']);
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { subscription, loading: subLoading, refetch } = useSubscription();
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [paddle, setPaddle] = useState<Paddle | null>(null);
  const [subscribingMonthly, setSubscribingMonthly] = useState(false);
  const [subscribingAnnual, setSubscribingAnnual] = useState(false);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);
  const [resumingSubscription, setResumingSubscription] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [updatingPayment, setUpdatingPayment] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      history.push('/login');
    }
  }, [authLoading, isAuthenticated, history]);

  useEffect(() => {
    // Initialize Paddle
    const initPaddle = async () => {
      try {
        const paddleInstance = await initializePaddle({
          environment:
            process.env.REACT_APP_PADDLE_ENVIRONMENT === 'production'
              ? 'production'
              : 'sandbox',
          token: process.env.REACT_APP_PADDLE_CLIENT_TOKEN || '',
        });
        setPaddle(paddleInstance as Paddle);
      } catch (error) {
        console.error('Error initializing Paddle:', error);
      }
    };

    initPaddle();
  }, [history]);

  const getRemainingTrialDays = (): number => {
    if (!subscription || subscription.type !== 'TRIAL') return 0;
    const end = new Date(subscription.currentPeriodEnd);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  // Helper function to get plan details from subscription
  const getPlanDetails = (): {
    planType: string;
    billingAmount: string;
    billingFrequency: string;
  } | null => {
    // First try to use the saved planType field
    if (subscription?.planType) {
      const isAnnual = subscription.planType === 'annual';
      return {
        planType: isAnnual ? t('plans.annual') : t('plans.monthly'),
        billingAmount: isAnnual ? '$19.99' : '$2.99',
        billingFrequency: isAnnual ? t('plans.perYear') : t('plans.perMonth'),
      };
    }

    // Fallback to parsing Paddle items if planType not available (legacy data)
    if (subscription?.items && subscription.items.length > 0) {
      const item = subscription.items[0];
      const interval = item.price.billing_cycle?.interval || 'month';
      const amount = item.price.unit_price?.amount || '0';

      // Convert amount from cents to dollars
      const amountInDollars = (parseInt(amount) / 100).toFixed(2);

      const planType =
        interval === 'year' ? t('plans.annual') : t('plans.monthly');
      const billingFrequency =
        interval === 'year' ? t('plans.perYear') : t('plans.perMonth');
      const billingAmount = `$${amountInDollars}`;

      return { planType, billingAmount, billingFrequency };
    }

    return null;
  };

  const handleCancelSubscription = () => {
    setShowCancelModal(true);
  };

  const confirmCancelSubscription = async () => {
    setCancelingSubscription(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await apiService.cancelSubscription();

      // Refetch subscription data
      await refetch();

      const endDate = subscription?.currentPeriodEnd;
      const formattedEndDate = endDate
        ? new Date(endDate).toLocaleDateString(i18n.language, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })
        : t('info.endOfPeriod');

      // Close modal and scroll to top to show success message
      setShowCancelModal(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setError(t('messages.cancelError'));
      console.error('Error canceling subscription:', error);
      setShowCancelModal(false);
    } finally {
      setCancelingSubscription(false);
    }
  };

  const handleResumeSubscription = () => {
    setShowResumeModal(true);
  };

  const confirmResumeSubscription = async () => {
    setResumingSubscription(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await apiService.resumeSubscription();

      // Refetch subscription data
      await refetch();

      // Display appropriate success message based on scenario
      let message;
      if (response.hadScheduledCancellation) {
        message = t('messages.cancellationRemoved');
      } else if (response.wasPaused) {
        message = t('messages.reactivated');
      } else {
        message = t('messages.updated');
      }

      setSuccessMessage(message);

      // Close modal and scroll to top to show success message
      setShowResumeModal(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error: any) {
      console.error('Error resuming subscription:', error);

      // Check if error is due to canceled subscription
      if (error.response?.data?.isCanceled) {
        setError(t('messages.cannotReactivate'));
      } else {
        setError(t('messages.resumeError'));
      }

      setShowResumeModal(false);
    } finally {
      setResumingSubscription(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    if (!paddle) {
      setError(t('messages.paymentUnavailable'));
      return;
    }

    setUpdatingPayment(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await apiService.updatePaymentMethod();

      console.log(
        'Opening Paddle checkout for payment update:',
        response.transactionId,
      );

      paddle.Checkout.open({
        transactionId: response.transactionId,
        settings: { successUrl: process.env.REACT_APP_PADDLE_RETURN_URL, locale: i18n.language },
      });
    } catch (error: any) {
      setError(t('messages.paymentError'));
      console.error('Error updating payment method:', error);
    } finally {
      setUpdatingPayment(false);
    }
  };

  const handleSubscribe = async (plan: PricingPlan) => {
    if (!paddle) {
      setError(t('messages.paymentUnavailable'));
      return;
    }

    if (plan === 'monthly') {
      setSubscribingMonthly(true);
    } else {
      setSubscribingAnnual(true);
    }

    setError(null);

    try {
      // Call backend to create checkout session with plan type
      const response = await apiService.createSubscription(plan);

      // Open Paddle checkout overlay
      paddle.Checkout.open({
        transactionId: response.transactionId,
        settings: {
          successUrl: process.env.REACT_APP_PADDLE_RETURN_URL,
          locale: i18n.language,
        },
      });
    } catch (error) {
      setError(t('messages.subscribeError'));
      console.error('Error creating subscription:', error);
    } finally {
      if (plan === 'monthly') {
        setSubscribingMonthly(false);
      } else {
        setSubscribingAnnual(false);
      }
    }
  };

  if (authLoading || subLoading) {
    return (
      <div
        className='min-h-screen flex items-center justify-center'
        style={{ background: 'rgb(8 6 20 / 100%)' }}
      >
        <div className='text-center animate-fade-in'>
          <div className='spinner glow mx-auto mb-6'></div>
          <p className='text-base' style={{ color: 'var(--text-secondary)' }}>
            {t('loading')}
          </p>
        </div>
      </div>
    );
  }

  const remainingDays = getRemainingTrialDays();
  const subscriptionStatus = subscription?.status;
  const isTrial = subscription?.type === 'TRIAL';
  const isPaid = subscription?.type === 'PAID';
  const trialActive = isTrial && remainingDays > 0;
  const planDetails = getPlanDetails();
  const isScheduledForCancellation =
    subscription?.cancelAtPeriodEnd === true &&
    subscription?.status !== 'canceled';

  // Determine page title and subtitle based on subscription type
  const getPageTitle = () => {
    if (isTrial) {
      return trialActive ? t('title.trialActive') : t('title.trialExpired');
    }

    if (isPaid) {
      if (subscriptionStatus === 'active') return t('title.active');
      if (subscriptionStatus === 'past_due') return t('title.updatePayment');
      if (subscriptionStatus === 'canceled') return t('title.canceled');
      if (subscriptionStatus === 'expired') return t('title.expired');
    }

    return t('title.subscribe');
  };

  const getPageSubtitle = () => {
    if (isTrial) {
      if (trialActive) {
        const dayLabel =
          remainingDays === 1 ? t('dayLabel.one') : t('dayLabel.other');
        return t('subtitle.trialActive', { days: remainingDays, dayLabel });
      }
      return t('subtitle.trialExpired');
    }
    if (isPaid) {
      if (subscriptionStatus === 'active') return t('subtitle.active');
      if (subscriptionStatus === 'past_due') return t('subtitle.pastDue');
      if (subscriptionStatus === 'canceled') return t('subtitle.canceled');
      if (subscriptionStatus === 'expired') return t('subtitle.expired');
    }
    return t('subtitle.default');
  };

  return (
    <div className='page'>
      <Header variant='player' />

      <div className='container-mobile'>
        <h1 className='suscription-title'>{getPageTitle()}</h1>
        <p
          className='text-base md:text-lg'
          style={{ color: 'var(--text-secondary)' }}
        >
          {getPageSubtitle()}
        </p>
        {successMessage && (
          <div className='subscription-success-message'>{successMessage}</div>
        )}
        {error && <div className='subscription-error-message'>{error}</div>}
        {isScheduledForCancellation && (
          <div className='warning-wrapper'>
            <h3 className='warning-title'>
              <svg
                style={{
                  width: '32px',
                }}
                className='w-7 h-7 text-white'
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                  clipRule='evenodd'
                />
              </svg>{' '}
              {t('scheduledCancellation.title')}
            </h3>
            <p className='warning-text'>
              {t('scheduledCancellation.message')}{' '}
              <i>
                {t('scheduledCancellation.keepAccessUntil')}{' '}
                <strong>
                  {subscription?.currentPeriodEnd
                    ? new Date(
                        subscription.currentPeriodEnd,
                      ).toLocaleDateString(i18n.language, {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })
                    : t('info.endOfPeriod')}
                </strong>
                . {t('scheduledCancellation.afterMessage')}
              </i>
            </p>
            <div
              className='flex'
              style={{
                justifyContent: 'flex-end',
              }}
            >
              <button
                onClick={handleResumeSubscription}
                disabled={resumingSubscription}
                className='btn-primary btn mt-6'
                style={{
                  width: 'fit-content',
                }}
              >
                {resumingSubscription ? (
                  t('resume.reactivating')
                ) : (
                  <>
                    <svg
                      className='w-5 h-5'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                      />
                    </svg>
                    {t('resume.reactivateNow')}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {subscriptionStatus === 'past_due' && (
          <div className='warning-wrapper mb-8'>
            <h3 className='warning-title'>
              <svg
                style={{ width: '32px' }}
                className='w-7 h-7 text-white'
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                  clipRule='evenodd'
                />
              </svg>
              {t('pastDue.title')}
            </h3>
            <p className='warning-text'>{t('pastDue.message')}</p>
            <div className='flex' style={{ justifyContent: 'flex-end' }}>
              <button
                onClick={handleUpdatePaymentMethod}
                disabled={updatingPayment || !paddle}
                className='btn-primary btn mt-6'
                style={{
                  width: 'fit-content',
                }}
              >
                {updatingPayment ? (
                  t('common:processing')
                ) : (
                  <>
                    <svg
                      className='w-5 h-5'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z'
                      />
                    </svg>
                    {t('pastDue.updateButton')}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
        {/* Trial Subscription Section */}
        {isTrial && trialActive && (
          <div className='dark-card' style={{ marginBottom: 24, marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>
                {t('trial.title')}
              </h3>
              <span style={{
                fontSize: 11, fontWeight: 700, color: 'rgba(147,197,253,0.9)',
                textTransform: 'uppercase', letterSpacing: '0.06em',
                padding: '4px 10px', borderRadius: 20,
                background: 'rgba(59,130,246,0.18)',
                border: '1px solid rgba(59,130,246,0.4)',
              }}>
                {t('trial.badge')}
              </span>
            </div>

            {subscription?.currentPeriodEnd && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: 16, borderRadius: 12,
                background: 'rgba(59,130,246,0.08)',
                border: '1px solid rgba(59,130,246,0.2)',
              }}>
                <div>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>
                    {t('trial.timeRemaining')}
                  </p>
                  <p style={{ fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.8)' }}>
                    {new Date(subscription.currentPeriodEnd).toLocaleDateString(
                      i18n.language,
                      { day: 'numeric', month: 'long', year: 'numeric' },
                    )}
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{
                    fontSize: 36, fontWeight: 700, lineHeight: 1,
                    color: 'rgba(147,197,253,0.95)',
                    fontFamily: 'Lexend Deca, sans-serif',
                  }}>
                    {remainingDays}
                  </p>
                  <p style={{ fontSize: 11, color: 'rgba(147,197,253,0.6)', marginTop: 2 }}>
                    {remainingDays === 1
                      ? t('trial.daysRemaining_one')
                      : t('trial.daysRemaining_other')}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Paid Subscription Section */}
        {isPaid &&
          (subscriptionStatus === 'active' ||
            subscriptionStatus === 'past_due') &&
          subscription && (
            <div className='suscription-info-wrapper mb-8'>
              <div className='flex items-center justify-between mb-4'>
                <h3 className='text-xl font-semibold'>
                  {subscriptionStatus === 'past_due'
                    ? t('info.subscriptionPending')
                    : t('info.yourSubscription')}
                </h3>
                <span
                  className={`badge text-white px-3 py-1 rounded-full text-sm ${
                    subscriptionStatus === 'past_due'
                      ? 'bg-red-500'
                      : isScheduledForCancellation
                        ? 'bg-orange-500'
                        : 'bg-green-500'
                  }`}
                >
                  {subscriptionStatus === 'past_due'
                    ? t('status.pastDue')
                    : isScheduledForCancellation
                      ? t('status.endingSoon')
                      : t('status.active')}
                </span>
              </div>

              <div className='space-y-4'>
                <div className='grid md:grid-cols-2 gap-4'>
                  <div className='p-4 bg-gray-50 dark:bg-gray-900/20 rounded-lg'>
                    <p className='text-sm text-gray-600 dark:text-gray-400 mb-1'>
                      {t('info.subscribedPlan')}
                    </p>
                    <p className='font-semibold text-lg'>
                      {planDetails?.planType || '-'}
                    </p>
                  </div>
                  <div className='p-4 bg-gray-50 dark:bg-gray-900/20 rounded-lg'>
                    <p className='text-sm text-gray-600 dark:text-gray-400 mb-1'>
                      {t('info.price')}
                    </p>
                    <p className='font-semibold text-lg'>
                      {planDetails
                        ? `${planDetails.billingAmount}${planDetails.billingFrequency}`
                        : '-'}
                    </p>
                  </div>
                </div>

                <div className='grid md:grid-cols-2 gap-4'>
                  <div className='p-4 bg-gray-50 dark:bg-gray-900/20 rounded-lg'>
                    <p className='text-sm text-gray-600 dark:text-gray-400 mb-1'>
                      {t('info.startDate')}
                    </p>
                    <p className='font-medium'>
                      {subscription.currentPeriodStart
                        ? new Date(
                            subscription.currentPeriodStart,
                          ).toLocaleDateString(i18n.language, {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })
                        : '-'}
                    </p>
                  </div>
                  <div
                    className={`p-4 rounded-lg ${
                      subscriptionStatus === 'past_due'
                        ? 'dark:bg-red-900/30 border-2 border-red-500'
                        : isScheduledForCancellation
                          ? 'bg-orange-100 dark:bg-orange-900/30 border-2 border-orange-500'
                          : 'bg-gray-50 dark:bg-gray-900/20'
                    }`}
                  >
                    <p
                      className={`text-sm mb-1 ${
                        subscriptionStatus === 'past_due'
                          ? 'text-red-900 dark:text-red-300 font-bold'
                          : isScheduledForCancellation
                            ? 'text-orange-900 dark:text-orange-300 font-bold'
                            : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {subscriptionStatus === 'past_due'
                        ? t('info.paymentDeclined')
                        : isScheduledForCancellation
                          ? t('info.activeTo')
                          : t('info.nextBilling')}
                    </p>
                    <p
                      className={`font-medium ${
                        subscriptionStatus === 'past_due'
                          ? 'text-sm text-red-900 dark:text-red-200 font-bold'
                          : isScheduledForCancellation
                            ? 'text-lg text-orange-900 dark:text-orange-200 font-bold'
                            : ''
                      }`}
                    >
                      {subscriptionStatus === 'past_due'
                        ? t('info.updatePaymentMethod')
                        : subscription.currentPeriodEnd
                          ? new Date(
                              subscription.currentPeriodEnd,
                            ).toLocaleDateString(i18n.language, {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                            })
                          : '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        {/* Pricing Plans - Show for trial users and users without subscription */}
        {(isTrial || !subscription) && !isPaid && (
          <div className='dark-card mt-3 mb-8 mt-8'>
            <h3
              style={{
                fontSize: 18,
                fontWeight: 700,
                textAlign: 'center',
                marginBottom: 4,
                fontFamily: 'Lexend Deca, sans-serif',
              }}
            >
              {isTrial
                ? t('plans.activateSubscription')
                : t('plans.choosePlan')}
            </h3>
            {isTrial && trialActive && (
              <p
                style={{
                  textAlign: 'center',
                  fontSize: 13,
                  color: 'rgba(255,255,255,0.5)',
                  marginBottom: 4,
                }}
              >
                {t('afterTrial')}
              </p>
            )}

            <div className='sub-plans'>
              {/* Monthly Plan */}
              <div className='sub-plan-card'>
                <div className='sub-plan-card__header'>
                  <div className='sub-plan-card__name'>
                    {t('plans.monthly')}
                  </div>
                  <div className='sub-plan-card__price'>
                    <span className='sub-plan-card__amount'>$2.99</span>
                    <span className='sub-plan-card__period'>
                      {t('plans.perMonth')}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleSubscribe('monthly')}
                  disabled={subscribingMonthly || !paddle}
                  className='btn btn-primary btn-block'
                >
                  {subscribingMonthly
                    ? t('common:processing')
                    : isTrial
                      ? t('plans.activateMonthly')
                      : t('plans.startMonthly')}
                </button>
              </div>

              {/* Annual Plan — featured */}
              <div className='sub-plan-card sub-plan-card--featured'>
                <div className='sub-plan-card__badge'>
                  {t('plans.save', { percent: 30 })}
                </div>
                <div className='sub-plan-card__header'>
                  <div className='sub-plan-card__name'>{t('plans.annual')}</div>
                  <div className='sub-plan-card__price'>
                    <span className='sub-plan-card__amount'>$24.99</span>
                    <span className='sub-plan-card__period'>
                      {t('plans.perYear')}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleSubscribe('annual')}
                  disabled={subscribingAnnual || !paddle}
                  className='btn btn-primary btn-block'
                >
                  {subscribingAnnual
                    ? t('common:processing')
                    : isTrial
                      ? t('plans.activateAnnual')
                      : t('plans.startAnnual')}
                </button>
              </div>
            </div>

            <p className='sub-trust'>
              ✓ {t('footer.cancelAnytime')} &nbsp;·&nbsp; ✓{' '}
              {t('footer.securePayment')}
            </p>
          </div>
        )}
        {/* Pricing Plans - Show for expired/canceled paid subscriptions */}
        {isPaid &&
          (subscriptionStatus === 'expired' ||
            subscriptionStatus === 'canceled') && (
            <div className='dark-card mb-8 mt-8'>
              <h3
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  textAlign: 'center',
                  marginBottom: 4,
                  fontFamily: 'Lexend Deca, sans-serif',
                }}
              >
                {t('plans.renewPremium')}
              </h3>

              <div className='sub-plans'>
                {/* Monthly Plan */}
                <div className='sub-plan-card'>
                  <div className='sub-plan-card__header'>
                    <div className='sub-plan-card__name'>
                      {t('plans.monthly')}
                    </div>
                    <div className='sub-plan-card__price'>
                      <span className='sub-plan-card__amount'>$2.99</span>
                      <span className='sub-plan-card__period'>
                        {t('plans.perMonth')}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleSubscribe('monthly')}
                    disabled={subscribingMonthly || !paddle}
                    className='btn btn-primary btn-block'
                  >
                    {subscribingMonthly
                      ? t('common:processing')
                      : t('plans.renewMonthly')}
                  </button>
                </div>

                {/* Annual Plan — featured */}
                <div className='sub-plan-card sub-plan-card--featured'>
                  <div className='sub-plan-card__badge'>
                    {t('plans.save', { percent: 30 })}
                  </div>
                  <div className='sub-plan-card__header'>
                    <div className='sub-plan-card__name'>
                      {t('plans.annual')}
                    </div>
                    <div className='sub-plan-card__price'>
                      <span className='sub-plan-card__amount'>$24.99</span>
                      <span className='sub-plan-card__period'>
                        {t('plans.perYear')}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleSubscribe('annual')}
                    disabled={subscribingAnnual || !paddle}
                    className='btn btn-primary btn-block'
                  >
                    {subscribingAnnual
                      ? t('common:processing')
                      : t('plans.renewAnnual')}
                  </button>
                </div>
              </div>

              <p className='sub-trust'>
                ✓ {t('footer.cancelAnytime')} &nbsp;·&nbsp; ✓{' '}
                {t('footer.securePayment')}
              </p>
            </div>
          )}
        {/* Cancel Subscription - Only for paid active subscriptions */}
        {isPaid &&
          subscriptionStatus === 'active' &&
          !isScheduledForCancellation && (
            <div className='cancel-subscription-section'>
              <h3 className='text-lg font-semibold mb-3'>
                {t('cancel.title')}
              </h3>
              <button
                onClick={handleCancelSubscription}
                disabled={cancelingSubscription}
                className='btn btn-secondary cancel-subscription-button'
              >
                {cancelingSubscription
                  ? t('cancel.canceling')
                  : t('cancel.button')}
              </button>
              <p className='cancel-subscription-end-of-period'>
                {t('cancel.keepAccessMessage')}
              </p>
            </div>
          )}
      </div>

      <Footer />

      <ConfirmationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={confirmCancelSubscription}
        title={t('cancel.modalTitle')}
        message={
          <>
            <p>{t('cancel.confirmMessage')}</p>
            <p>
              {t('cancel.keepAccessUntil')}{' '}
              <strong>
                {subscription?.currentPeriodEnd
                  ? new Date(subscription.currentPeriodEnd).toLocaleDateString(
                      i18n.language,
                      {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      },
                    )
                  : t('info.endOfPeriod')}
              </strong>
              .
            </p>
            <p>{t('cancel.afterCancelMessage')}</p>
          </>
        }
        confirmText={t('cancel.confirmButton')}
        cancelText={t('cancel.keepButton')}
        variant='warning'
        isLoading={cancelingSubscription}
      />

      <ConfirmationModal
        isOpen={showResumeModal}
        onClose={() => setShowResumeModal(false)}
        onConfirm={confirmResumeSubscription}
        title={t('resume.modalTitle')}
        message={t('resume.confirmMessage')}
        confirmText={t('resume.confirmButton')}
        cancelText={t('resume.cancelButton')}
        variant='info'
        isLoading={resumingSubscription}
      />
    </div>
  );
};

export default SubscriptionPage;
